/**
 * A patient's laboratory results, as the patient page groups and flags them
 * (z8nrz7g57g).
 *
 * The LIS interface sends no panel category, so the filter chips are decided
 * here: by the HL7 diagnostic service section (OBR-24) when the laboratory
 * sends one, else by the test's name. A result whose order named a product is
 * that product's specimen; every other result is the patient's own. Pure, so
 * the grouping, flags and the CD34+ trend are tested without a DOM.
 */
import type { LisObservation, PatientLisResult } from '@/api/lisApi'
import type { PatientHistoryEvent } from '@/api/patientsApi'
import type { Tone } from './productRecord'

export type LisCategory = 'infectious' | 'hematology' | 'cell_counts' | 'microbiology' | 'typing' | 'other'

export const LIS_CATEGORY_LABEL: Record<LisCategory, string> = {
  infectious: 'Infectious disease',
  hematology: 'Hematology',
  cell_counts: 'Cell counts',
  microbiology: 'Microbiology',
  typing: 'Typing & HLA',
  other: 'Other',
}

/** HL7 table 0074 diagnostic service sections, as far as they decide a chip. */
const SECTION_CATEGORY: Record<string, LisCategory> = {
  HM: 'hematology',
  MB: 'microbiology',
  MCB: 'microbiology',
  MYC: 'microbiology',
  BB: 'typing',
  SR: 'infectious',
  VR: 'infectious',
  IMM: 'infectious',
}

/** By name, most specific first: "CD34+ of CD45+" is a cell count even though a CBC may list CD34. */
const NAME_RULES: [RegExp, LisCategory][] = [
  [/\bhla\b|\babo\b|\brh\b|blood type|typing|antibody screen/i, 'typing'],
  // \bgram\b: "Gram stain", never the end of "Hemogram".
  [/steril|culture|\bgram\b|bacter|fung|myco|endotox/i, 'microbiology'],
  [/hbs|hbc|hcv|hiv|htlv|syph|pallid|treponem|cmv|\bnat\b|west nile|wnv|cruzi|chagas|zika|idm|infectious|serolog|hepatitis/i, 'infectious'],
  [/\btnc\b|nucleated|viab|7-?aad|cd34|cd3\b|cd45|cell count|recovery|ishage/i, 'cell_counts'],
  [/\bcbc\b|\bwbc\b|hemoglobin|\bhgb\b|platelet|\bplt\b|hematocrit|\bhct\b|neutrophil|lymph/i, 'hematology'],
]

/** Which chip a result belongs under. */
export function categoryOf(result: Pick<PatientLisResult, 'serviceSection' | 'testName' | 'testCode' | 'observations'>): LisCategory {
  const section = result.serviceSection?.trim().toUpperCase()
  if (section && SECTION_CATEGORY[section]) return SECTION_CATEGORY[section]
  const names = [result.testName, result.testCode].filter(Boolean).join(' ')
  for (const [pattern, category] of NAME_RULES) if (pattern.test(names)) return category
  const observed = result.observations.map((o) => o.codeText ?? o.code).join(' ')
  for (const [pattern, category] of NAME_RULES) if (pattern.test(observed)) return category
  return 'other'
}

/** A result's specimen: the patient's own, or a product's (its order named one). */
export function isProductSpecimen(result: Pick<PatientLisResult, 'productId' | 'din'>): boolean {
  return !!(result.productId || result.din)
}

export type LisFilter = 'all' | 'patient' | 'product' | LisCategory

export interface LisChip {
  filter: LisFilter
  label: string
  count: number
}

/**
 * The filter chips, each with the panels it would show. "All" always; the
 * others only when they would show something, so no chip leads to nothing.
 */
export function lisChips(results: readonly PatientLisResult[]): LisChip[] {
  const chips: LisChip[] = [{ filter: 'all', label: 'All', count: results.length }]
  const patient = results.filter((r) => !isProductSpecimen(r)).length
  const product = results.length - patient
  if (patient) chips.push({ filter: 'patient', label: 'Patient specimens', count: patient })
  if (product) chips.push({ filter: 'product', label: 'Product (DIN)', count: product })
  const byCategory = new Map<LisCategory, number>()
  for (const r of results) byCategory.set(categoryOf(r), (byCategory.get(categoryOf(r)) ?? 0) + 1)
  for (const category of Object.keys(LIS_CATEGORY_LABEL) as LisCategory[]) {
    const count = byCategory.get(category)
    if (count) chips.push({ filter: category, label: LIS_CATEGORY_LABEL[category], count })
  }
  return chips
}

export function filterLis(results: readonly PatientLisResult[], filter: LisFilter): PatientLisResult[] {
  if (filter === 'all') return [...results]
  if (filter === 'patient') return results.filter((r) => !isProductSpecimen(r))
  if (filter === 'product') return results.filter(isProductSpecimen)
  return results.filter((r) => categoryOf(r) === filter)
}

/**
 * An HL7 result status (OBR-25 / OBX-11) that is not final yet: preliminary,
 * not verified, in the laboratory, or partial. The one rule for the status
 * pill, the flag and the history, so none calls a result done that another
 * calls preliminary.
 */
export function isPreliminary(code: string | null | undefined): boolean {
  return ['P', 'R', 'I', 'S'].includes(code?.trim().toUpperCase() ?? '')
}

/** HL7 result status (OBR-25 / OBX-11) as a pill. */
export function resultStatus(code: string | null | undefined): { label: string; tone: Tone } {
  if (isPreliminary(code)) return { label: 'Preliminary', tone: 'amber' }
  switch (code?.trim().toUpperCase()) {
    case 'F':
      return { label: 'Final', tone: 'gray' }
    case 'C':
      return { label: 'Corrected', tone: 'blue' }
    case 'X':
    case 'D':
      return { label: 'Cancelled', tone: 'gray' }
    default:
      return { label: code?.trim() || '—', tone: 'gray' }
  }
}

export type FlagTone = 'high' | 'low' | 'normal' | 'pending' | 'none'

/**
 * An observation's flag (OBX-8). "Pending" is a preliminary result with no
 * flag yet — not a normal one. No flag on a final result is "—", never
 * "Normal": the laboratory did not say so.
 */
export function flagOf(o: Pick<LisObservation, 'abnormalFlag' | 'status'>, resultStatusCode?: string | null): { label: string; tone: FlagTone } {
  const flag = o.abnormalFlag?.trim().toUpperCase()
  if (flag === 'H' || flag === 'HH' || flag === '>') return { label: flag === 'HH' ? 'Critical high' : 'High', tone: 'high' }
  if (flag === 'L' || flag === 'LL' || flag === '<') return { label: flag === 'LL' ? 'Critical low' : 'Low', tone: 'low' }
  if (flag === 'A' || flag === 'AA') return { label: 'Abnormal', tone: 'high' }
  if (flag === 'N') return { label: 'Normal', tone: 'normal' }
  if (flag) return { label: flag, tone: 'none' }
  if (isPreliminary(o.status ?? resultStatusCode)) return { label: 'Pending', tone: 'pending' }
  return { label: '—', tone: 'none' }
}

/** Results flagged high, low or abnormal. */
export function flaggedCount(results: readonly PatientLisResult[]): number {
  return results.reduce(
    (n, r) => n + r.observations.filter((o) => ['high', 'low'].includes(flagOf(o, r.resultStatus).tone)).length,
    0,
  )
}

/** When the specimen was taken: the earliest observation time, else when the result was sent, else received. */
export function collectedAt(result: Pick<PatientLisResult, 'observations' | 'sentAt' | 'createdAt'>): string {
  const observed = result.observations
    .map((o) => o.observedAt)
    .filter((t): t is string => !!t)
    .sort()
  return observed[0] ?? result.sentAt ?? result.createdAt
}

/** The latest time any result reached Smart-CGT — what the page can say about "last sync". */
export function lastReceived(results: readonly Pick<PatientLisResult, 'createdAt'>[]): string | null {
  return results.reduce<string | null>((latest, r) => (!latest || r.createdAt > latest ? r.createdAt : latest), null)
}

export interface Cd34Point {
  at: string
  value: number
  units: string | null
}

/**
 * The patient's peripheral blood CD34+ counts over time, for the mobilization
 * chart: patient specimens only (a product's CD34 is a different measure),
 * observations naming CD34 with a number, one per day (the latest that day),
 * oldest first. `threshold` is read from the reference range when it states
 * a lower bound ("≥ 20", ">=20", "> 20 cells/uL").
 */
export function peripheralCd34(results: readonly PatientLisResult[]): { points: Cd34Point[]; threshold: number | null } {
  const byDay = new Map<string, Cd34Point>()
  let threshold: number | null = null
  for (const r of results) {
    if (isProductSpecimen(r)) continue
    for (const o of r.observations) {
      const name = `${o.codeText ?? ''} ${o.code}`
      if (!/cd34/i.test(name) || /%|pct|percent|of cd45/i.test(`${name} ${o.units ?? ''}`)) continue
      // A blank value is no value yet, not 0: Number('') is 0.
      const raw = o.value?.trim()
      const value = o.valueNumeric ?? (raw ? Number(raw) : NaN)
      if (!Number.isFinite(value)) continue
      const at = o.observedAt ?? r.sentAt ?? r.createdAt
      const day = at.slice(0, 10)
      const known = byDay.get(day)
      if (!known || at > known.at) byDay.set(day, { at, value, units: o.units })
      if (threshold == null && o.referenceRange) threshold = lowerBound(o.referenceRange)
    }
  }
  return { points: [...byDay.values()].sort((a, b) => a.at.localeCompare(b.at)), threshold }
}

/** The lower bound a reference range states ("≥ 20", ">=20", "20-"), or null. */
export function lowerBound(range: string): number | null {
  const m = /(?:≥|>=|>)\s*(\d+(?:\.\d+)?)/.exec(range) ?? /^\s*(\d+(?:\.\d+)?)\s*-\s*$/.exec(range)
  return m ? Number(m[1]) : null
}

/** The history keeps the newest this many events, as the server does. */
export const HISTORY_LIMIT = 200

/**
 * The patient's history with their laboratory results in it (z8nrz7g57g).
 *
 * The server builds the history from the patient's runs, forms and products
 * but leaves results out: the page already loads them for its LIS section, so
 * they are added here rather than read a second time. Each result is the moment
 * the laboratory sent it, with its DIN when the specimen was a product's.
 * Newest first, a dated-only event at the end of its day (the server's rule),
 * cut to the newest HISTORY_LIMIT.
 */
export function historyWithResults(
  history: readonly PatientHistoryEvent[],
  results: readonly PatientLisResult[] | undefined,
): PatientHistoryEvent[] {
  const fromResults: PatientHistoryEvent[] = (results ?? []).map((r) => {
    const preliminary = isPreliminary(r.resultStatus)
    return {
      at: r.sentAt ?? r.createdAt,
      dateOnly: false,
      state: preliminary ? 'in_progress' : 'done',
      title: `Laboratory result received: ${r.testName ?? r.testCode}`,
      detail: preliminary ? 'Preliminary' : null,
      productId: r.productId,
      din: r.productId ? r.din : null,
    }
  })
  const time = (e: PatientHistoryEvent) => Date.parse(e.dateOnly ? `${e.at}T23:59:59Z` : e.at)
  return [...history, ...fromResults]
    .map((e, i) => ({ e, i }))
    .sort((a, b) => time(b.e) - time(a.e) || a.i - b.i)
    .map(({ e }) => e)
    .slice(0, HISTORY_LIMIT)
}
